/**
 * ================================================================
 * API SERVICE: AuthenticationService
 * ================================================================
 *
 * @service          authApi
 * @description      API service for user authentication and session management
 * @baseUrl          /user/*
 *
 * @endpoints
 * - POST   /user/login - User authentication with credentials and FCM token
 * - GET    /user/logout - User session termination
 *
 * @authentication  AuthToken header added after successful login
 * @rateLimit        Standard API rate limiting applies
 *
 * @caching
 * - Strategy: No caching for auth operations (security)
 * - Session: Managed via session storage and global error handler
 *
 * @errorHandling
 * - Global handler: transformApiError for consistent error management
 * - Auth errors: Special handling to prevent recursive redirects
 * - Network errors: User-friendly error messages
 *
 * @types
 * - LoginCredentials: Login request structure with FCM token (from @/types/auth)
 * - LoginResponse: Authentication response structure (from @/types/auth)
 * - ApiError: Standardized error response format (from @/types/common)
 *
 * @firebaseIntegration
 * - FCM Token: Automatically included in login request when available
 * - Token generation: Handled by FCM token manager
 * - Fallback: Login proceeds even if FCM token generation fails
 *
 * ================================================================
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiErrorTransformer, createBaseQuery, handleApiSuccess } from '@/lib/api/baseQuery';
import type { LoginCredentials, LoginResponse } from '@/types/auth/auth';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: createBaseQuery(
    import.meta.env.VITE_API_BASE_URL || 'https://web.arhamshare.com/apb/v2',
  ),
  endpoints: (builder) => ({
    /**
     * User authentication with branch code, password, and FCM token
     *
     * @endpoint POST /user/login
     * @param {LoginCredentials} credentials - User login credentials
     * @returns {LoginResponse} Authentication tokens and user data
     * @cache No caching for security reasons
     * @fcmIntegration FCM token included in credentials when available
     */
    login: builder.mutation<LoginResponse, LoginCredentials>({
      query: (credentials) => ({
        url: '/user/login',
        method: 'POST',
        body: {
          branch_code: credentials.branch_code,
          password: credentials.password,
          user_type: credentials.user_type || 'self',
          ...(credentials.fcm_token && { fcm_token: credentials.fcm_token }),
        },
      }),

      // ================================================================
      // ERROR TRANSFORMATION
      // ================================================================
      //
      // Purpose: Handle login errors without triggering auth redirects
      // Special case: Login errors should not redirect to login page
      // Implementation: Use global error handler with modified options
      // ================================================================

      transformErrorResponse: createApiErrorTransformer('AuthLogin', false),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('AuthLogin', `Login successfully.`, false);
        } catch {
          // Error handled globally
        }
      },
    }),

    /**
     * User session termination and cleanup
     *
     * @endpoint GET /user/logout
     * @returns {void} Empty response on successful logout
     * @cache No caching for security reasons
     */
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/user/logout',
        method: 'GET',
      }),

      // ================================================================
      // ERROR TRANSFORMATION
      // ================================================================
      //
      // Purpose: Handle logout errors gracefully
      // Implementation: Use global error handler for consistency
      // Note: Logout should generally succeed even with network issues
      // ================================================================

      transformErrorResponse: createApiErrorTransformer('AuthLogout', false),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('AuthLogout', 'Logged out successfully.', false);
        } catch {
          // Error handled globally
        }
      },
    }),

    // ================================================================
    // FUTURE AUTHENTICATION ENDPOINTS
    // ================================================================
    //
    // Note: Commented endpoints for future implementation
    // - Token refresh: For automatic session renewal
    // - Token validation: For session verification
    // - Password reset: For user account recovery
    // ================================================================

    // refreshToken: builder.mutation<RefreshTokenResponse, string>({
    //   query: (refreshToken) => ({
    //     url: '/auth/refresh',
    //     method: 'POST',
    //     body: { refreshToken },
    //   }),
    //   transformErrorResponse: transformApiError,
    // }),

    // validateToken: builder.query<boolean, void>({
    //   query: () => ({
    //     url: '/auth/validate',
    //     method: 'GET',
    //   }),
    //   transformErrorResponse: transformApiError,
    // }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  // useRefreshTokenMutation,
  // useValidateTokenQuery,
} = authApi;

/**
 * ================================================================
 * END OF SERVICE: AuthenticationService
 * ================================================================
 *
 * @exports          authApi and authentication-related mutation hooks
 * @dependencies     RTK Query, base query configuration, auth types
 * @usedBy           Login components, auth guards, session management
 *
 * @maintenance
 * - Security: No caching for authentication operations
 * - Error handling: Special handling for auth-specific errors
 * - Session: Integration with global error handler for session cleanup
 *
 * @security
 * - Credentials: Secure transmission of login credentials
 * - Session management: Proper token handling and cleanup
 * - Error exposure: Safe error messages without credential leakage
 *
 * ================================================================
 */
